import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Typography, Box,
  FormControl, InputLabel, Select, MenuItem, Chip, FormControlLabel, Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { LoadingButton } from '@mui/lab';
import { COMPANY_TYPE, companyTypeOptions } from '../../../utils/Enums';

const EditCompanyModal = ({ open, onClose, company, onSave }) => {
  const { t } = useTranslation();

  const normalizeTemplateId = (templateId) => {
    if (templateId === 'completo') return 'ecommerce1';
    if (templateId === 'desborde') return 'ecommerce2';
    return templateId || 'basico';
  };
  
  const [editedCompany, setEditedCompany] = useState({
    name: '',
    domain: '',
    siteDomains: '',
    templateId: 'basico',
    companyType: COMPANY_TYPE.FOOD_SERVICE,
    isActive: true
  });

  const [siteDomainsInput, setSiteDomainsInput] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const templateOptions = [
    { value: 'basico', label: 'Básico' },
    { value: 'ecommerce1', label: 'Ecommerce 1' },
    { value: 'ecommerce2', label: 'Ecommerce 2' }
  ];

  useEffect(() => {
    if (company) {
      setEditedCompany({
        name: company.name || '',
        domain: company.domain || '',
        siteDomains: company.siteDomains || '',
        templateId: normalizeTemplateId(company.templateId),
        companyType: company.companyType || COMPANY_TYPE.FOOD_SERVICE,
        isActive: company.isActive !== false
      });
      // Convertir site_domains separados por ; a formato de input
      setSiteDomainsInput(company.siteDomains ? company.siteDomains.split(';').join('; ') : '');
    }
  }, [company]);

  const handleFieldChange = (field, value) => {
    setEditedCompany(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSiteDomainsChange = (value) => {
    setSiteDomainsInput(value);
    // Convertir a formato con ; para guardar
    const domains = value.split(';').map(d => d.trim()).filter(d => d);
    handleFieldChange('siteDomains', domains.join(';'));
    setErrors(prev => ({ ...prev, siteDomains: '' }));
  };

  const validateFields = () => {
    const newErrors = {};

    if (!editedCompany.name || editedCompany.name.trim() === '') {
      newErrors.name = t('company_name_required');
    }

    if (!editedCompany.domain || editedCompany.domain.trim() === '') {
      newErrors.domain = t('domain_required');
    }
    // siteDomains es opcional, no requiere validación

    if (!editedCompany.templateId) {
      newErrors.templateId = t('template_required') || 'Template requerido';
    }

    if (!editedCompany.companyType) {
      newErrors.companyType = t('company_type_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateFields()) {
      setSaving(true);
      try {
        const statusChanged = company && (company.isActive !== editedCompany.isActive);
        const revokeActiveTokens = statusChanged
          ? window.confirm('¿Deseas vencer todos los tokens activos de esta empresa? Deberán iniciar sesión nuevamente.')
          : false;

        const companyData = {
          id: company.id,
          name: editedCompany.name,
          domain: editedCompany.domain,
          siteDomains: editedCompany.siteDomains || null,
          templateId: editedCompany.templateId,
          companyType: editedCompany.companyType,
          isActive: editedCompany.isActive,
          revokeActiveTokens
        };
        await onSave(companyData);
      } catch (error) {
        console.error('Error updating company:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  const siteDomains = siteDomainsInput.split(';').map(d => d.trim()).filter(d => d);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getIcon('settings')}
          {t('edit_company')}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              {t('basic_data')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('company_name')}
              fullWidth
              value={editedCompany.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.companyType} required>
              <InputLabel>{t('company_type')}</InputLabel>
              <Select
                value={editedCompany.companyType}
                onChange={(e) => handleFieldChange('companyType', e.target.value)}
                label={t('company_type')}
              >
                {companyTypeOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {t(option.label.toLowerCase().replace(' ', '_')) || option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.companyType && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.companyType}
              </Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('domain') || 'Dominio (Login)'}
              fullWidth
              value={editedCompany.domain}
              onChange={(e) => handleFieldChange('domain', e.target.value)}
              error={!!errors.domain}
              helperText={errors.domain || 'Dominio único para login'}
              placeholder="ejemplo.com"
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.templateId} required>
              <InputLabel>{t('template_id') || 'Template'}</InputLabel>
              <Select
                value={editedCompany.templateId}
                onChange={(e) => handleFieldChange('templateId', e.target.value)}
                label={t('template_id') || 'Template'}
              >
                {templateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.templateId && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.templateId}
              </Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('site_domains') || 'Dominios del Sitio Web'}
              fullWidth
              multiline
              rows={3}
              value={siteDomainsInput}
              onChange={(e) => handleSiteDomainsChange(e.target.value)}
              error={!!errors.siteDomains}
              helperText={errors.siteDomains || 'Separar múltiples dominios con punto y coma (;). Opcional.'}
              placeholder="ejemplo.com; www.ejemplo.com; otro-dominio.com"
            />
            {siteDomains.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {siteDomains.map((domain, idx) => (
                  <Chip key={idx} label={domain} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Estado
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={editedCompany.isActive}
                  onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                />
              }
              label={editedCompany.isActive ? 'Empresa activa' : 'Empresa bloqueada (bloquea login)'}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton 
          variant="contained" 
          onClick={handleSave}
          loading={saving}
        >
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditCompanyModal;
